import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { type SupportedLanguage } from '@/shared/config/languages'
import { filterTranslationLocalesExcludingSupportedSource } from '@artem-lex/compliance-documents'
import { isExtractPreviewSectionNotFound } from '../lib/extractionPreviewUi'
import { getSectionSourceLocaleFromPreview } from '../lib/sectionSourceLocale'
import {
  EXTRACT_DIALOG_STEPS_SCOPE,
  EXTRACT_DIALOG_STEPS_TRANSLATABLE,
  resolvePostExtractDialogOutcome,
  resolveSkippedDialogSteps,
} from '../lib/standardSectionExtractionFlow'
import { normalizeRiskReductionMeasuresForPatch } from '@/shared/hooks/api/standards'
import {
  type ExtractPreviewResponse,
  type SectionLocalizeStreamResult,
  type StandardExtractSection,
  type UserStandard,
  useExtractPreview,
  useSectionLocalizeStream,
  useUpdateUserStandardInformationForUse,
  useUpdateUserStandardRiskReductionMeasures,
  useUpdateUserStandardScope,
  useUpdateUserStandardSignificantHazards,
} from '@/shared/hooks/api/userStandards'

export type ExtractDialogStep =
  | 'extract'
  | 'acknowledgeNotFound'
  | 'review'
  | 'translate'
  | 'reviewTranslations'
  | 'save'

export {
  EXTRACT_DIALOG_STEPS_SCOPE,
  EXTRACT_DIALOG_STEPS_TRANSLATABLE,
  EXTRACT_DIALOG_TRANSLATION_STEPS,
} from '../lib/standardSectionExtractionFlow'

const APPLY_SUCCESS_KEYS: Record<StandardExtractSection, string> = {
  scope: 'detail.scopeExtractApplySuccess',
  significantHazards: 'detail.hazardsExtractApplySuccess',
  riskReductionMeasures: 'detail.rrmsExtractApplySuccess',
  informationForUse: 'detail.ifuExtractApplySuccess',
}

function mergeSectionLocalizeIntoPreview(
  preview: ExtractPreviewResponse,
  localized: SectionLocalizeStreamResult
): ExtractPreviewResponse {
  if (localized.section === 'significantHazards' && localized.significantHazards) {
    return { ...preview, significantHazards: localized.significantHazards }
  }
  if (
    localized.section === 'riskReductionMeasures' &&
    localized.riskReductionMeasures
  ) {
    return {
      ...preview,
      riskReductionMeasures: localized.riskReductionMeasures,
    }
  }
  if (localized.section === 'informationForUse' && localized.informationForUse) {
    return { ...preview, informationForUse: localized.informationForUse }
  }
  return preview
}

export function useStandardSectionExtraction(
  standard: UserStandard,
  section: StandardExtractSection
) {
  const { t } = useTranslation('standardsLibrary')

  const [translationLocales, setTranslationLocales] = useState<
    SupportedLanguage[]
  >([])
  const [extractDialogOpen, setExtractDialogOpen] = useState(false)
  const [dialogStep, setDialogStep] = useState<ExtractDialogStep>('extract')
  const [previewData, setPreviewData] = useState<ExtractPreviewResponse | null>(
    null
  )
  const [extractErrorMessage, setExtractErrorMessage] = useState<string | null>(
    null
  )
  const [translateProgressMessage, setTranslateProgressMessage] = useState<
    string | null
  >(null)
  const [hasCompletedTranslation, setHasCompletedTranslation] = useState(false)

  const extractPreviewMutation = useExtractPreview(standard.id)
  const sectionLocalizeStream = useSectionLocalizeStream(standard.id)
  const updateScope = useUpdateUserStandardScope(standard.id)
  const updateSignificantHazards = useUpdateUserStandardSignificantHazards(
    standard.id
  )
  const updateRiskReductionMeasures =
    useUpdateUserStandardRiskReductionMeasures(standard.id)
  const updateInformationForUse = useUpdateUserStandardInformationForUse(
    standard.id
  )

  const isApplying =
    updateScope.isPending ||
    updateSignificantHazards.isPending ||
    updateRiskReductionMeasures.isPending ||
    updateInformationForUse.isPending

  const supportsTranslation = section !== 'scope'

  const dialogSteps = supportsTranslation
    ? EXTRACT_DIALOG_STEPS_TRANSLATABLE
    : EXTRACT_DIALOG_STEPS_SCOPE

  const sectionNotFoundFlow = useMemo(() => {
    if (previewData == null) {
      return false
    }
    return isExtractPreviewSectionNotFound(section, previewData)
  }, [previewData, section])

  const skippedDialogSteps = useMemo(
    () => resolveSkippedDialogSteps(sectionNotFoundFlow, supportsTranslation),
    [sectionNotFoundFlow, supportsTranslation]
  )

  const resetDialogState = useCallback(() => {
    setPreviewData(null)
    setDialogStep('extract')
    setExtractErrorMessage(null)
    setTranslateProgressMessage(null)
    setHasCompletedTranslation(false)
    setTranslationLocales([])
  }, [])

  const closeDialog = useCallback(() => {
    setExtractDialogOpen(false)
    resetDialogState()
  }, [resetDialogState])

  const runExtractAndOpenPreview = useCallback(async () => {
    setExtractErrorMessage(null)
    setTranslateProgressMessage(null)
    try {
      const result = await extractPreviewMutation.mutateAsync({
        extractSections: [section],
      })
      setPreviewData(result)
      const outcome = resolvePostExtractDialogOutcome(section, result, t)
      if (outcome.kind === 'failed') {
        setExtractErrorMessage(outcome.errorMessage)
        setHasCompletedTranslation(false)
        setDialogStep('extract')
        return
      }
      if (outcome.kind === 'notFound') {
        setHasCompletedTranslation(false)
        setDialogStep('acknowledgeNotFound')
        return
      }

      setHasCompletedTranslation(false)
      setDialogStep('review')
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('detail.extractPreview.extractError')
      setExtractErrorMessage(message)
      setPreviewData(null)
      setHasCompletedTranslation(false)
      setDialogStep('extract')
    }
  }, [extractPreviewMutation, section, t])

  const persistPreview = useCallback(async () => {
    if (!previewData) {
      return
    }

    if (section === 'scope') {
      const scopeText = previewData.scope?.trim() ?? ''
      if (scopeText.length === 0) {
        if (isExtractPreviewSectionNotFound(section, previewData)) {
          await updateScope.mutateAsync({ scope: null })
        }
        return
      }
      await updateScope.mutateAsync({ scope: scopeText })
    } else if (section === 'significantHazards') {
      await updateSignificantHazards.mutateAsync({
        hazards: previewData.significantHazards?.hazards ?? [],
        sourceLocale: previewData.significantHazards?.sourceLocale,
      })
    } else if (section === 'riskReductionMeasures') {
      await updateRiskReductionMeasures.mutateAsync({
        measures: normalizeRiskReductionMeasuresForPatch(
          previewData.riskReductionMeasures?.measures ?? []
        ),
        sourceLocale: previewData.riskReductionMeasures?.sourceLocale,
      })
    } else if (section === 'informationForUse') {
      const ifuNotFound =
        previewData.informationForUseImpediment?.reason === 'ifu_not_found'
      await updateInformationForUse.mutateAsync({
        clauses: previewData.informationForUse?.clauses ?? [],
        noRequirements:
          ifuNotFound ||
          previewData.informationForUse?.noRequirements === true,
        sourceLocale: previewData.informationForUse?.sourceLocale,
      })
    }
  }, [
    previewData,
    section,
    updateScope,
    updateSignificantHazards,
    updateRiskReductionMeasures,
    updateInformationForUse,
  ])

  const handleSave = useCallback(async () => {
    if (!previewData) {
      return
    }
    try {
      await persistPreview()
      closeDialog()
      toast.success(t(APPLY_SUCCESS_KEYS[section]))
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('detail.extractPreview.applyError')
      toast.error(message)
    }
  }, [previewData, persistPreview, closeDialog, section, t])

  const runTranslateStream = useCallback(async () => {
    if (!previewData || !supportsTranslation) {
      return
    }
    if (translationLocales.length === 0) {
      toast.error(t('detail.extractDialog.translationLocalesRequired'))
      return
    }
    const sourceLocale =
      previewData != null
        ? getSectionSourceLocaleFromPreview(section, previewData)
        : undefined
    const effectiveTranslationLocales =
      filterTranslationLocalesExcludingSupportedSource(
        sourceLocale,
        translationLocales
      )
    if (effectiveTranslationLocales.length === 0) {
      toast.error(t('detail.extractDialog.translationLocalesRequired'))
      return
    }
    setTranslateProgressMessage(null)
    try {
      const localized = await sectionLocalizeStream.mutateAsync({
        section,
        translationLocales: effectiveTranslationLocales,
        preview: previewData,
        onProgress: (message) => {
          setTranslateProgressMessage(message)
        },
      })
      setPreviewData((prev) =>
        prev != null ? mergeSectionLocalizeIntoPreview(prev, localized) : prev
      )
      setHasCompletedTranslation(true)
      setDialogStep('reviewTranslations')
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : t('detail.extractDialog.translateError')
      toast.error(message)
    } finally {
      setTranslateProgressMessage(null)
    }
  }, [
    previewData,
    supportsTranslation,
    translationLocales,
    section,
    sectionLocalizeStream,
    t,
  ])

  const skipTranslate = useCallback(() => {
    setHasCompletedTranslation(false)
    setDialogStep('save')
  }, [])

  const openExtractDialog = useCallback(() => {
    resetDialogState()
    setExtractDialogOpen(true)
  }, [resetDialogState])

  const handleExtractDialogOpenChange = useCallback(
    (open: boolean) => {
      setExtractDialogOpen(open)
      if (!open) {
        resetDialogState()
      }
    },
    [resetDialogState]
  )

  const goToSaveStep = useCallback(() => {
    setDialogStep('save')
  }, [])

  const advanceFromReview = useCallback(() => {
    if (!supportsTranslation) {
      setDialogStep('save')
      return
    }
    setDialogStep('translate')
  }, [supportsTranslation])

  const advanceFromAcknowledgeNotFound = useCallback(() => {
    void handleSave()
  }, [handleSave])

  const goBack = useCallback(() => {
    if (dialogStep === 'save') {
      if (hasCompletedTranslation) {
        setDialogStep('reviewTranslations')
      } else {
        setDialogStep('translate')
      }
      return
    }
    if (dialogStep === 'acknowledgeNotFound') {
      setPreviewData(null)
      setExtractErrorMessage(null)
      setDialogStep('extract')
      return
    }
    if (dialogStep === 'reviewTranslations') {
      if (hasCompletedTranslation) {
        setDialogStep('translate')
      } else {
        setDialogStep('review')
      }
      return
    }
    if (dialogStep === 'translate') {
      setDialogStep('review')
      return
    }
    if (dialogStep === 'review') {
      setPreviewData(null)
      setExtractErrorMessage(null)
      setDialogStep('extract')
    }
  }, [dialogStep, hasCompletedTranslation, sectionNotFoundFlow])

  return {
    section,
    supportsTranslation,
    sectionNotFoundFlow,
    skippedDialogSteps,
    dialogSteps,
    translationLocales,
    setTranslationLocales,
    extractDialogOpen,
    openExtractDialog,
    handleExtractDialogOpenChange,
    dialogStep,
    previewData,
    extractErrorMessage,
    translateProgressMessage,
    hasCompletedTranslation,
    runExtractAndOpenPreview,
    runTranslateStream,
    skipTranslate,
    handleSave,
    goToSaveStep,
    advanceFromReview,
    advanceFromAcknowledgeNotFound,
    goBack,
    closeDialog,
    isExtracting: extractPreviewMutation.isPending,
    isTranslating: sectionLocalizeStream.isPending,
    isApplying,
    sectionSourceLocale:
      previewData != null
        ? getSectionSourceLocaleFromPreview(section, previewData)
        : undefined,
  }
}

export type StandardSectionExtractionState = ReturnType<
  typeof useStandardSectionExtraction
>
